import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  alpha,
  styled,
} from '@mui/material';
import {
  Hub as GraphIcon,
  Search as SearchIcon,
  ZoomIn as ZoomIcon,
  Explore as ExploreIcon,
} from '@mui/icons-material';

/**
 * Props for GraphOnboardingModal
 */
interface GraphOnboardingModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when modal is closed
   */
  onClose: () => void;

  /**
   * Callback when user clicks "Don't show again"
   */
  onDontShowAgain?: () => void;
}

/**
 * Styled container for feature cards
 */
const FeatureCard = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  marginBottom: theme.spacing(2),
}));

/**
 * Onboarding steps
 */
const onboardingSteps = [
  {
    label: 'Explore Your Knowledge',
    icon: <GraphIcon />,
    description: 'Visualize your conversations, documents, and topics as an interactive network. See how everything connects.',
  },
  {
    label: 'Search & Filter',
    icon: <SearchIcon />,
    description: 'Use natural language queries to find specific nodes or filter by type (messages, documents, topics).',
  },
  {
    label: 'Navigate the Graph',
    icon: <ZoomIcon />,
    description: 'Zoom, pan, and click on nodes to explore details. Click edges to see relationships between items.',
  },
  {
    label: 'Discover Connections',
    icon: <ExploreIcon />,
    description: 'Find unexpected connections between your ideas, conversations, and documents.',
  },
];

/**
 * GraphOnboardingModal Component
 *
 * First-time user onboarding modal for Knowledge Graph feature.
 * Explains the graph visualization and how to use it.
 *
 * Week 5 Feature: Graph View Beta Launch
 */
const GraphOnboardingModal: React.FC<GraphOnboardingModalProps> = ({
  open,
  onClose,
  onDontShowAgain,
}) => {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    onClose();
  };

  const handleDontShowAgain = () => {
    if (onDontShowAgain) {
      onDontShowAgain();
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 8,
        },
      }}
    >
      <DialogTitle style={{ paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <GraphIcon color="primary" style={{ fontSize: 32 }} />
            <div>
              <Typography variant="h5" fontWeight={600}>
                Welcome to Knowledge Graph
              </Typography>
              <Chip
                label="BETA"
                size="small"
                style={{
                  marginTop: 4,
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(156, 39, 176, 0.2)',
                  color: '#9c27b0',
                }}
              />
            </div>
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom style={{ marginBottom: 24 }}>
          Your conversations, documents, and topics are more connected than you think.
          The Knowledge Graph helps you discover and explore these connections visually.
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          {onboardingSteps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor:
                        activeStep === index
                          ? '#1976d2'
                          : 'rgba(255,255,255,0.1)',
                      color: activeStep === index ? 'white' : 'rgba(0,0,0,0.6)',
                    }}
                  >
                    {step.icon}
                  </div>
                )}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                  {step.description}
                </Typography>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    style={{ marginTop: 8, marginRight: 8 }}
                  >
                    {index === onboardingSteps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    style={{ marginTop: 8, marginRight: 8 }}
                  >
                    Back
                  </Button>
                </div>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === onboardingSteps.length && (
          <FeatureCard>
            <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <GraphIcon color="primary" />
              You're All Set!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start exploring your knowledge graph. Use the search bar to find specific items,
              or simply browse the visualization to discover connections.
            </Typography>
          </FeatureCard>
        )}
      </DialogContent>

      <DialogActions style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 16 }}>
        <Button onClick={handleDontShowAgain} size="small">
          Don't show again
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={handleClose} variant="contained">
          {activeStep === onboardingSteps.length ? 'Get Started' : 'Skip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GraphOnboardingModal;
